// File: src/lib/graphql/queries/task.ts
// Purpose: Task Management GraphQL Queries and Mutations
// VIBE - Task API

import { gql } from 'graphql-request'
import { executeGraphQL } from '../client'

// ============================================
// TYPES
// ============================================

export interface Task {
  id: string
  task?: string
  title?: string
  description?: string
  note?: string
  status: TaskStatus
  priority: TaskPriority
  type?: TaskType
  category?: string
  date?: string
  startTime?: string
  endTime?: string
  totalHours?: number
  tags?: string[]
  // JIRA/Issue tracking fields
  issueKey?: string
  storyPoints?: number
  originalEstimate?: number
  remainingEstimate?: number
  timeSpent?: number
  boardPosition?: number
  commentCount?: number
  resolution?: string
  isOverdue?: boolean
  isBlocked?: boolean
  project?: {
    _id?: string
    id?: string
    name: string
    code: string
    description?: string
  }
  employee?: {
    azureId?: string
    azureTenantId?: string
    azureEmail?: string
    azureDisplayName?: string
    ssoEnabled?: boolean
    lastAzureLogin?: string
    azureGroups?: string[]
    _id: string
    firstName: string
    lastName: string
    email: string
    countryCode?: string
    mobile_number?: string
    languages?: string[]
    joining_date?: string
    skills?: string[]
    designation?: string
    department?: string
    shift?: number
    is_active: boolean
    is_deleted?: boolean
    createdAt: string
    updatedAt: string
  }
  assignee?: {
    id: string
    firstName: string
    lastName: string
    employeeId: string
  }
  assignedBy?: {
    _id: string
    firstName: string
    lastName: string
    email: string
    createdAt: string
    updatedAt: string
  }
  reporter?: {
    id: string
    firstName: string
    lastName: string
  }
  dueDate?: string
  startDate?: string
  completedDate?: string
  estimatedHours?: number
  actualHours?: number
  attachments?: number
  comments?: number
  dependencies?: string[]
  isActive?: boolean
  createdAt: string
  updatedAt: string
  reassignedAt?: string
  reassignmentReason?: string
  isReassigned?: boolean
  reassignmentHistory?: {
    reassignedAt: string
    reason: string
    status: string
    completedAt: string
    lastStatusUpdatedAt: string
    fromEmployee: {
      _id: string
      firstName: string
      lastName: string
      email: string
    }
    toEmployee: {
      _id: string
      firstName: string
      lastName: string
      email: string
    }
  }[]
}

export type TaskStatus = 'todo' | 'in-progress' | 'review' | 'completed' | 'cancelled' | 'blocked'
export type TaskPriority = 'low' | 'medium' | 'high' | 'critical'
export type TaskType = 'feature' | 'bug' | 'enhancement' | 'research' | 'documentation' | 'testing'

export interface TaskSummary {
  totalTasks: number
  completedTasks: number
  inProgressTasks: number
  overdueTasks: number
  todayTasks: number
  weekTasks: number
}

export interface DailyTasksResponse {
  date: string
  totalTasks: number
  completedTasks: number
  totalHours: number
  tasks: Task[]
}

export interface TaskAssignment {
  id: string
  task: Task
  project: {
    id: string
    name: string
  }
  assignee: {
    id: string
    firstName: string
    lastName: string
  }
  assignedDate: string
  dueDate?: string
  completedDate?: string
  status: TaskStatus
}

// ============================================
// QUERIES
// ============================================

export const TASKS_QUERY = gql`
  query Tasks(
    $filter: TaskFilterInput
    $limit: Int
    $offset: Int
  ) {
    tasks(
      filter: $filter
      limit: $limit
      offset: $offset
    ) {
      id
      task
      status
      date
      startTime
      endTime
      totalHours
      description
      note
      priority
      category
      tags
      reassignedAt
      reassignmentReason
      isReassigned
      createdAt
      updatedAt
      employee {
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        is_active
        is_deleted
        createdAt
        updatedAt
      }
      project {
        _id
        name
        code
        description
        status
        priority
        startDate
        endDate
        actualStartDate
        actualEndDate
        tags
        progress
        is_active
        is_deleted
        createdAt
        updatedAt
      }
      reassignmentHistory {
        reassignedAt
        reason
        status
        completedAt
        lastStatusUpdatedAt
        toEmployee {
          _id
          firstName
          email
          lastName
        }
        fromEmployee {
          firstName
          lastName
          email
          _id
        }
      }
    }
  }
`

export const TASK_QUERY = gql`
  query Task($id: ID!) {
    task(id: $id) {
      id
      title
      description
      status
      priority
      type
      project {
        id
        name
        code
      }
      assignee {
        id
        firstName
        lastName
        employeeId
        email
      }
      reporter {
        id
        firstName
        lastName
        email
      }
      dueDate
      startDate
      completedDate
      estimatedHours
      actualHours
      tags
      attachments
      comments
      dependencies
      isActive
      createdAt
      updatedAt
    }
  }
`

export const MY_TASKS_QUERY = gql`
  query MyTasks($filter: TaskFilterInput, $first: Int, $after: Int) {
    myTasks(filter: $filter, limit: $first, offset: $after) {
      id
      task
      status
      date
      startTime
      endTime
      totalHours
      description
      note
      priority
      category
      tags
      reassignedAt
      reassignmentReason
      isReassigned
      createdAt
      updatedAt
      employee {
        _id
        firstName
        lastName
        email
      }
      project {
        _id
        name
        code
        description
        is_active
        createdAt
        updatedAt
      }
      assignedBy {
        _id
        firstName
        lastName
        email
        createdAt
        updatedAt
      }
      reassignmentHistory {
        reassignedAt
        reason
        status
        completedAt
        lastStatusUpdatedAt
        fromEmployee {
          _id
          firstName
          lastName
        }
        toEmployee {
          _id
          firstName
          lastName
        }
      }
    }
  }
`

export const TASK_SUMMARY_QUERY = gql`
  query TaskSummary($employeeId: ID!, $startDate: String!, $endDate: String!) {
    taskSummary(employeeId: $employeeId, startDate: $startDate, endDate: $endDate) {
      totalTasks
      completedTasks
      inProgressTasks
      todoTasks
      cancelledTasks
      onHoldTasks
      delayTasks
      totalHours
      averageHoursPerTask
      completionRate
    }
  }
`

export const DAILY_TASKS_QUERY = gql`
  query DailyTasks($employeeId: ID!, $date: String!) {
    dailyTasks(employeeId: $employeeId, date: $date) {
      date
      totalTasks
      completedTasks
      totalHours
      tasks {
        id
        task
        status
        date
        startTime
        endTime
        totalHours
        description
        note
        priority
        category
        tags
        createdAt
        updatedAt
        employee {
          azureId
          azureTenantId
          azureEmail
          azureDisplayName
          ssoEnabled
          lastAzureLogin
          azureGroups
          _id
          firstName
          lastName
          email
          countryCode
          mobile_number
          languages
          joining_date
          skills
          designation
          department
          shift
          is_active
          is_deleted
          createdAt
          updatedAt
        }
        project {
          _id
          name
          code
          description
        }
      }
    }
  }
`

export const TASKS_BY_PROJECT_QUERY = gql`
  query TasksByProject($projectId: ID!, $status: TaskStatus) {
    tasksByProject(projectId: $projectId, status: $status) {
      edges {
        node {
          id
          title
          status
          priority
          assignee {
            firstName
            lastName
          }
          dueDate
          progress
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

// ============================================
// MUTATIONS
// ============================================

export const CREATE_TASK_MUTATION = gql`
  mutation CreateTask($input: TaskInput!) {
    createTask(input: $input) {
      id
      task
      status
      date
      startTime
      endTime
      totalHours
      description
      note
      priority
      category
      tags
      issueKey
      storyPoints
      originalEstimate
      remainingEstimate
      timeSpent
      boardPosition
      commentCount
      resolution
      dueDate
      completedDate
      isOverdue
      isBlocked
      reassignedAt
      reassignmentReason
      isReassigned
      createdAt
      updatedAt
    }
  }
`

export const UPDATE_TASK_MUTATION = gql`
  mutation UpdateTask(
    $id: ID!
    $input: TaskUpdateInput!
  ) {
    updateTask(
      id: $id
      input: $input
    ) {
      id
      task
      status
      date
      startTime
      endTime
      totalHours
      description
      note
      priority
      category
      tags
      createdAt
      updatedAt
      employee {
        azureId
        azureTenantId
        azureEmail
        azureDisplayName
        ssoEnabled
        lastAzureLogin
        azureGroups
        _id
        firstName
        lastName
        email
        countryCode
        mobile_number
        languages
        joining_date
        skills
        designation
        department
        shift
        is_active
        is_deleted
        createdAt
        updatedAt
      }
    }
  }
`

export const DELETE_TASK_MUTATION = gql`
  mutation DeleteTask($id: ID!) {
    deleteTask(id: $id)
  }
`

export const START_TASK_MUTATION = gql`
  mutation StartTask($id: ID!) {
    startTask(id: $id) {
      id
      status
      startDate
    }
  }
`

export const COMPLETE_TASK_MUTATION = gql`
  mutation CompleteTask($id: ID!) {
    completeTask(id: $id) {
      id
      task
      status
      date
    }
  }
`

export const REOPEN_TASK_MUTATION = gql`
  mutation ReopenTask($id: ID!) {
    reopenTask(id: $id) {
      id
      task
      status
      date
      startTime
      endTime
      totalHours
      description
      priority
      category
      tags
      createdAt
      updatedAt
    }
  }
`

export const ASSIGN_TASK_MUTATION = gql`
  mutation AssignTask($id: ID!, $employeeId: ID!, $reason: String) {
    assignTask(id: $id, employeeId: $employeeId, reason: $reason) {
      id
      task
      status
      date
      startTime
      endTime
      totalHours
      description
      priority
      category
      tags
      reassignedAt
      reassignmentReason
      isReassigned
      createdAt
      updatedAt
    }
  }
`

export const DELAY_TASK_MUTATION = gql`
  mutation DelayTask($id: ID!) {
    delayTask(id: $id) {
      id
      task
      status
      date
      startTime
      endTime
      totalHours
      description
      priority
      category
      tags
      reassignedAt
      reassignmentReason
      isReassigned
      createdAt
      updatedAt
    }
  }
`

export const BULK_UPDATE_TASK_STATUS_MUTATION = gql`
  mutation BulkUpdateTaskStatus($taskIds: [ID!]!, $status: TaskStatus!) {
    bulkUpdateTaskStatus(taskIds: $taskIds, status: $status) {
      updatedCount
      tasks {
        id
        status
      }
    }
  }
`

export const REASSIGN_COMPLETED_TASK_MUTATION = gql`
  mutation ReassignCompletedTask($taskId: ID!, $newEmployeeId: ID!, $reason: String!) {
    reassignCompletedTask(input: { taskId: $taskId, newEmployeeId: $newEmployeeId, reason: $reason }) {
      id
      task
      status
      date
      startTime
      endTime
      totalHours
      description
      priority
      category
      tags
      reassignedAt
      reassignmentReason
      isReassigned
      createdAt
      updatedAt
    }
  }
`

// ============================================
// API SERVICE CLASS
// ============================================

export class TaskAPI {
  // Task Queries
  static async getTasks(filter?: any, limit?: number, offset?: number): Promise<Task[]> {
    const { tasks } = await executeGraphQL<{
      tasks: Task[]
    }>(
      TASKS_QUERY,
      { filter, limit, offset }
    )
    return tasks
  }

  // Get daily tasks with date filter and optional employee filter
  static async getDailyTasksWithFilter(date: string, employeeId?: string): Promise<Task[]> {
    const filter: any = {
      startDate: date,
      endDate: date
    }

    if (employeeId) {
      filter.employee = employeeId
    }

    const tasks = await this.getTasks(filter, 100, 0)
    return tasks
  }

  // Get tasks by date range with optional employee filter
  static async getTasksByDateRange(startDate: string, endDate: string, employeeId?: string): Promise<Task[]> {
    const filter: any = {
      startDate: startDate,
      endDate: endDate
    }

    if (employeeId) {
      filter.employee = employeeId
    }

    const tasks = await this.getTasks(filter, 1000, 0) // Increased limit for date ranges
    return tasks
  }

  static async getTask(id: string): Promise<Task> {
    const { task } = await executeGraphQL<{ task: Task }>(
      TASK_QUERY,
      { id }
    )
    return task
  }

  static async getMyTasks(
    filter: any,
    first: number = 10,
    after?: number
  ): Promise<any> {
    if (!filter.employee) {
      filter.employee = JSON.parse(localStorage.getItem("instadev-auth") || '{}').userId ?? "";
    }
    console.log("filter", filter, "first", first, "after", after)
    const { myTasks } = await executeGraphQL<{
      myTasks: any
    }>(
      MY_TASKS_QUERY,
      { filter, first, after }
    )
    return myTasks
  }

  static async getTaskSummary(employeeId: string, startDate: string, endDate: string): Promise<TaskSummary> {
    const { taskSummary } = await executeGraphQL<{ taskSummary: TaskSummary }>(
      TASK_SUMMARY_QUERY,
      { employeeId, startDate, endDate }
    )
    return taskSummary
  }

  static async getDailyTasks(date: string, employeeId: string): Promise<DailyTasksResponse> {
    const { dailyTasks } = await executeGraphQL<{ dailyTasks: DailyTasksResponse }>(
      DAILY_TASKS_QUERY,
      { employeeId, date }
    )
    return dailyTasks
  }


  static async getTasksByProject(
    projectId: string,
    status?: TaskStatus
  ): Promise<{
    edges: { node: Task; cursor: string }[];
    pageInfo: { hasNextPage: boolean; endCursor?: string };
    totalCount: number;
  }> {
    const { tasksByProject } = await executeGraphQL<{
      tasksByProject: {
        edges: { node: Task; cursor: string }[];
        pageInfo: { hasNextPage: boolean; endCursor?: string };
        totalCount: number;
      }
    }>(
      TASKS_BY_PROJECT_QUERY,
      { projectId, status }
    )
    return tasksByProject
  }

  // Task Mutations
  static async createTask(taskData: {
    task?: string;
    status?: string;
    date?: string;
    startTime: string; // Required field
    endTime?: string | null;
    description?: string;
    note?: string;
    employee?: string;
    project?: string;
    assignedBy?: string;
    priority?: string;
    category?: string;
  }): Promise<Task> {
    const input = {
      task: taskData.task,
      status: taskData.status,
      date: taskData.date,
      startTime: taskData.startTime,
      endTime: taskData.endTime || undefined,
      description: taskData.description,
      note: taskData.note,
      employee: taskData.employee,
      project: taskData.project,
      priority: taskData.priority,
      category: taskData.category
    }
    const { createTask } = await executeGraphQL<{ createTask: Task }>(
      CREATE_TASK_MUTATION,
      { input }
    )
    return createTask
  }

  static async updateTask(id: string, input: any): Promise<Task> {
    const { updateTask } = await executeGraphQL<{ updateTask: Task }>(
      UPDATE_TASK_MUTATION,
      { id, input }
    )
    return updateTask
  }

  static async deleteTask(id: string): Promise<boolean> {
    try {
      await executeGraphQL<{ deleteTask: any }>(
        DELETE_TASK_MUTATION,
        { id }
      )
      return true
    } catch (error) {
      console.error('Error deleting task:', error)
      throw error
    }
  }

  static async startTask(id: string): Promise<Task> {
    const { startTask } = await executeGraphQL<{ startTask: Task }>(
      START_TASK_MUTATION,
      { id }
    )
    return startTask
  }

  static async completeTask(id: string): Promise<Task> {
    const { completeTask } = await executeGraphQL<{ completeTask: Task }>(
      COMPLETE_TASK_MUTATION,
      { id }
    )
    return completeTask
  }

  static async reopenTask(id: string): Promise<Task> {
    const { reopenTask } = await executeGraphQL<{ reopenTask: Task }>(
      REOPEN_TASK_MUTATION,
      { id }
    )
    return reopenTask
  }

  static async assignTask(id: string, employeeId: string, reason?: string): Promise<Task> {
    const { assignTask } = await executeGraphQL<{ assignTask: Task }>(
      ASSIGN_TASK_MUTATION,
      { id, employeeId, reason }
    )
    return assignTask
  }

  static async delayTask(id: string): Promise<Task> {
    const { delayTask } = await executeGraphQL<{ delayTask: Task }>(
      DELAY_TASK_MUTATION,
      { id }
    )
    return delayTask
  }

  static async bulkUpdateTaskStatus(
    taskIds: string[],
    status: TaskStatus
  ): Promise<{
    updatedCount: number;
    tasks: { id: string; status: TaskStatus }[];
  }> {
    const { bulkUpdateTaskStatus } = await executeGraphQL<{
      bulkUpdateTaskStatus: {
        updatedCount: number;
        tasks: { id: string; status: TaskStatus }[];
      }
    }>(
      BULK_UPDATE_TASK_STATUS_MUTATION,
      { taskIds, status }
    )
    return bulkUpdateTaskStatus
  }

  static async reassignCompletedTask(taskId: string, newEmployeeId: string, reason: string): Promise<Task> {
    const { reassignCompletedTask } = await executeGraphQL<{ reassignCompletedTask: Task }>(
      REASSIGN_COMPLETED_TASK_MUTATION,
      { taskId, newEmployeeId, reason }
    )
    return reassignCompletedTask
  }

  // Alias for dashboard compatibility - calls getMyTasks
  static async getEmployeeTasks(filter: object, first: number = 10, after?: number) {
    return TaskAPI.getMyTasks(filter, first, after)
  }
}